/**
 * MainMenuScene.js - Main Menu Scene for Memex Racing
 * 
 * Provides the main menu interface with Quick Play, Create Room, and Join Room options.
 * Integrates with the authentication system and displays user information.
 */

import Phaser from 'phaser';
import { AuthenticatedScene } from './AuthenticatedScene';
import { GameplayGuide } from '../../ui/components/GameplayGuide.js';

export class MainMenuScene extends AuthenticatedScene {
    constructor() {
        super({ key: 'MainMenuScene' });
        this.selectedOption = 0; // For keyboard navigation
        this.menuOptions = [];
        this.userInfo = null;
        this.gameplayGuide = null;
    }

    init() {
        console.log('[MainMenuScene] Init phase - resetting camera');
        
        // Reset the initialized flag
        this.isInitialized = false;
        
        // Completely reset camera state before scene creates
        if (this.cameras && this.cameras.main) {
            // Clear all effects
            this.cameras.main.clearFX();
            // Reset alpha
            this.cameras.main.setAlpha(1);
            // Immediately fade in from black to ensure visibility
            this.cameras.main.fadeIn(0);
            // Set background color
            this.cameras.main.setBackgroundColor('#001122');
            
            console.log('[MainMenuScene] Camera reset completed in init');
        }
    }

    create() {
        console.log('[MainMenuScene] Starting create phase...');
        console.log('[MainMenuScene] Environment:', process.env.NODE_ENV || 'production');
        
        // Get music manager - it should already be playing from LoginScene
        const musicManager = this.game.registry.get('musicManager');
        if (musicManager && !musicManager.isPlaying) {
            console.log('[MainMenuScene] Resuming background music');
            musicManager.startMusic();
        }
        
        // Prevent duplicate scene initialization
        if (this.isInitialized) {
            console.log('[MainMenuScene] Already initialized, skipping duplicate create');
            return;
        }
        this.isInitialized = true;
        
        // Additional camera state reset to ensure visibility
        if (this.cameras && this.cameras.main) {
            this.cameras.main.setAlpha(1);
            this.cameras.main.clearFX();
            this.cameras.main.setBackgroundColor('#001122');
            // Ensure camera is fully visible
            this.cameras.main.fadeIn(0);
        }
        
        try {
            // Call parent create for authentication setup
            console.log('[MainMenuScene] Calling parent create...');
            super.create();
            console.log('[MainMenuScene] ✅ Parent create completed');
            
            // Initialize GameplayGuide
            console.log('[MainMenuScene] Initializing GameplayGuide...');
            this.gameplayGuide = new GameplayGuide(this);
            console.log('[MainMenuScene] ✅ GameplayGuide initialized');
            
            // Get actual camera dimensions for responsive background
            const gameWidth = this.cameras.main.width;
            const gameHeight = this.cameras.main.height;
            const centerX = gameWidth / 2;
            const centerY = gameHeight / 2;
            
            console.log(`[MainMenuScene] Using fixed dimensions: ${gameWidth}x${gameHeight}`);
            console.log(`[MainMenuScene] Center point: (${centerX}, ${centerY})`);
            
            // Set background image
            console.log('[MainMenuScene] Setting background...');
            
            // COMPREHENSIVE background texture validation
            console.log('[MainMenuScene] 🔍 Checking background texture availability...');
            console.log('[MainMenuScene] Available textures:', Object.keys(this.textures.list));
            
            let backgroundLoaded = false;
            let backgroundTexture = null;
            
            // Primary: Check for main_menu_bg
            if (this.textures.exists('main_menu_bg')) {
                backgroundTexture = this.textures.get('main_menu_bg');
                const source = backgroundTexture.source[0];
                console.log('[MainMenuScene] ✅ main_menu_bg found:', {
                    width: source.width,
                    height: source.height,
                    complete: source.complete,
                    src: source.image?.src || 'unknown'
                });
                
                // Verify it's not an error texture (1x1 pixels)
                if (source.width > 1 && source.height > 1) {
                    console.log('[MainMenuScene] Adding main menu background...');
                    const mainBg = this.add.image(centerX, centerY, 'main_menu_bg');
                    // Scale to cover the entire screen while maintaining aspect ratio
                    const scaleX = gameWidth / mainBg.width;
                    const scaleY = gameHeight / mainBg.height;
                    const scale = Math.max(scaleX, scaleY);
                    mainBg.setScale(scale);
                    mainBg.setOrigin(0.5, 0.5); // Ensure it's centered
                    mainBg.setDepth(-20);
                    backgroundLoaded = true;
                    console.log(`[MainMenuScene] ✅ Background scaled: ${scale.toFixed(2)} for ${gameWidth}x${gameHeight}`);
                } else {
                    console.error('[MainMenuScene] 🚨 main_menu_bg is only 1x1 pixels - corrupt/failed load!');
                }
            }
            
            // Fallback 1: Check for backup texture
            if (!backgroundLoaded && this.textures.exists('main_menu_bg_backup')) {
                console.warn('[MainMenuScene] 🔄 Using backup texture: main_menu_bg_backup');
                const mainBg = this.add.image(centerX, centerY, 'main_menu_bg_backup');
                const scaleX = gameWidth / mainBg.width;
                const scaleY = gameHeight / mainBg.height;
                const scale = Math.max(scaleX, scaleY);
                mainBg.setScale(scale);
                mainBg.setOrigin(0.5, 0.5);
                mainBg.setDepth(-20);
                backgroundLoaded = true;
                console.log(`[MainMenuScene] ✅ Backup background loaded and scaled`);
            }
            
            // Fallback 2: Check for emergency loaded textures
            if (!backgroundLoaded && this.textures.exists('main_menu_bg_emergency')) {
                console.warn('[MainMenuScene] 🆘 Using emergency texture: main_menu_bg_emergency');
                const mainBg = this.add.image(centerX, centerY, 'main_menu_bg_emergency');
                const scaleX = gameWidth / mainBg.width;
                const scaleY = gameHeight / mainBg.height;
                const scale = Math.max(scaleX, scaleY);
                mainBg.setScale(scale);
                mainBg.setOrigin(0.5, 0.5);
                mainBg.setDepth(-20);
                backgroundLoaded = true;
                console.log(`[MainMenuScene] ✅ Emergency background loaded and scaled`);
            }
            
            // Fallback 3: Check for second emergency texture
            if (!backgroundLoaded && this.textures.exists('main_menu_bg_emergency2')) {
                console.warn('[MainMenuScene] 🆘 Using emergency texture: main_menu_bg_emergency2');
                const mainBg = this.add.image(centerX, centerY, 'main_menu_bg_emergency2');
                const scaleX = gameWidth / mainBg.width;
                const scaleY = gameHeight / mainBg.height;
                const scale = Math.max(scaleX, scaleY);
                mainBg.setScale(scale);
                mainBg.setOrigin(0.5, 0.5);
                mainBg.setDepth(-20);
                backgroundLoaded = true;
                console.log(`[MainMenuScene] ✅ Emergency backup background loaded and scaled`);
            }
            
            if (!backgroundLoaded) {
                console.error('[MainMenuScene] ❌ NO BACKGROUND TEXTURE AVAILABLE - main_menu_bg missing!');
                console.error('[MainMenuScene] Will fall back to animated rainbow background');
            }
            
            // Add very subtle overlay for better text visibility
            const overlay = this.add.graphics();
            overlay.fillStyle(0x000000, 0.3);
            overlay.fillRect(0, 0, gameWidth, gameHeight);
            overlay.setDepth(-19);
            
            // Only create vibrant background if no background image loaded successfully
            if (!backgroundLoaded) {
                console.warn('[MainMenuScene] 🌈 Creating animated rainbow background as fallback');
                this.createVibrantMemeBackground();
            } else {
                console.log('[MainMenuScene] ✅ Using proper background image - skipping animated fallback');
            }
            
            // Skip meme collage overlay when background image is present
            
            // Main title
            console.log('[MainMenuScene] Creating title...');
            const title = this.add.text(centerX, gameHeight * 0.185, 'M1 PUG RACING', {
                fontSize: '96px',
                fontFamily: 'Courier New, Consolas, monospace',
                color: '#00ff00',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 8,
                shadow: {
                    offsetX: 2,
                    offsetY: 2,
                    color: '#000000',
                    blur: 4,
                    stroke: true,
                    fill: true
                }
            }).setOrigin(0.5);
            
            // Subtitle
            const subtitle = this.add.text(centerX, gameHeight * 0.26, 'FIND THE M TOKEN', {
                fontSize: '32px',
                fontFamily: 'Courier New, Consolas, monospace',
                color: '#ffff00',
                fontWeight: 'bold'
            }).setOrigin(0.5);
            
            // Animate title
            console.log('[MainMenuScene] Creating title animation...');
            this.tweens.add({
                targets: title,
                scaleX: 1.05,
                scaleY: 1.05,
                duration: 2000,
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });
            
            // Create Notice Panel
            console.log('[MainMenuScene] Creating notice panel...');
            this.createNoticePanel();
            
            // Create Walkthrough Panel
            console.log('[MainMenuScene] Creating walkthrough panel...');
            this.createWalkthroughPanel();
            
            // Create menu buttons
            console.log('[MainMenuScene] Creating menu buttons...');
            this.createMenuButtons();
            
            // User info panel removed - no longer needed
            // The username is shown in the login system instead
            
            // Version info with better visibility
            this.add.text(50, 1030, `Version: ${process.env.VERSION || '0.1.0'}`, {
                fontSize: '18px',
                fontFamily: 'Consolas, "Courier New", monospace',
                color: '#00ff00',
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 2
            });
            
            // Instructions removed per user request to prevent overlay with settings
            
            // Setup input handlers
            console.log('[MainMenuScene] Setting up input handlers...');
            this.setupInputHandlers();
            
            // Play ambient music if available
            console.log('[MainMenuScene] Playing ambient music...');
            this.playAmbientMusic();
            
            // Ensure camera is visible and properly reset
            if (this.cameras && this.cameras.main) {
                this.cameras.main.setAlpha(1);
                this.cameras.main.clearFX();
                this.cameras.main.setBackgroundColor('#001122');
                // Ensure full visibility
                this.cameras.main.fadeIn(0);
            }
            
            // Smooth fade in transition
            console.log('[MainMenuScene] Starting fade in...');
            if (this.cameras && this.cameras.main) {
                this.cameras.main.fadeFrom(500, 0, 0, 0);
            }
            
            // Emit event for integration
            console.log('[MainMenuScene] Emitting MAIN_MENU_READY event...');
            this.events.emit('MAIN_MENU_READY');
            
            console.log('[MainMenuScene] ✅ Create phase completed successfully');
            
        } catch (error) {
            console.error('[MainMenuScene] ❌ Error in create phase:', error);
            console.error('[MainMenuScene] Error stack:', error.stack);
            
            // Create a minimal fallback UI
            this.createFallbackUI(error);
        }
    }

    /**
     * Create notice panel for announcements
     */
    createNoticePanel() {
        // Create notice container
        const noticeContainer = this.add.container(960, 350);
        noticeContainer.setDepth(100);
        
        // Background for notice
        const noticeBg = this.add.graphics();
        noticeBg.fillStyle(0x000000, 0.9);
        noticeBg.lineStyle(2, 0x00ff00, 1);
        noticeBg.fillRoundedRect(-400, -40, 800, 80, 10);
        noticeBg.strokeRoundedRect(-400, -40, 800, 80, 10);
        noticeContainer.add(noticeBg);
        
        // Notice title
        const noticeTitle = this.add.text(-350, -20, '📢 ANNOUNCEMENT', {
            fontSize: '18px',
            fontFamily: 'Courier New, Consolas, monospace',
            color: '#00ffff',
            fontWeight: 'bold'
        });
        noticeContainer.add(noticeTitle);
        
        // Notice text
        const noticeText = this.add.text(-350, 5, 'Welcome to Season 1! Double tokens weekend is active!', {
            fontSize: '16px',
            fontFamily: 'Courier New, Consolas, monospace',
            color: '#ffffff',
            wordWrap: { width: 700 }
        });
        noticeContainer.add(noticeText);
        
        // Store references for updates
        this.noticeContainer = noticeContainer;
        this.noticeTitle = noticeTitle;
        this.noticeText = noticeText;
        
        // Load notices from notification manager if available
        this.loadNotices();
        
        // Pulse animation for notice
        this.tweens.add({
            targets: noticeBg,
            alpha: 0.8,
            duration: 2000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }
    
    /**
     * Load notices from notification manager
     */
    loadNotices() {
        // Check if notification manager exists in window
        if (typeof window !== 'undefined' && window.notificationManager) {
            const activeNotices = window.notificationManager.getActiveNotices();
            if (activeNotices && activeNotices.length > 0) {
                this.currentNoticeIndex = 0;
                this.notices = activeNotices;
                this.displayNotice(this.notices[0]);
                
                // Rotate notices if multiple
                if (activeNotices.length > 1) {
                    this.time.addEvent({
                        delay: 8000,
                        callback: () => this.rotateNotices(),
                        loop: true
                    });
                }
            }
        }
    }
    
    /**
     * Display a notice
     */
    displayNotice(notice) {
        if (!notice || !this.noticeTitle || !this.noticeText) return;
        
        const icons = {
            info: 'ℹ️',
            warning: '⚠️',
            alert: '🚨',
            announcement: '📢',
            event: '🎉',
            maintenance: '🔧',
            update: '🔄'
        };
        
        const icon = icons[notice.type] || '📢';
        this.noticeTitle.setText(`${icon} ${notice.title}`);
        this.noticeText.setText(notice.message);
        
        // Update colors based on type
        const colors = {
            info: '#00ffff',
            warning: '#ffff00',
            alert: '#ff0000',
            announcement: '#00ff00',
            event: '#ff00ff',
            maintenance: '#ffa500',
            update: '#00ffff'
        };
        
        this.noticeTitle.setColor(colors[notice.type] || '#00ffff');
    }
    
    /**
     * Rotate through notices
     */
    rotateNotices() {
        if (!this.notices || this.notices.length <= 1) return;
        
        this.currentNoticeIndex = (this.currentNoticeIndex + 1) % this.notices.length;
        this.displayNotice(this.notices[this.currentNoticeIndex]);
    }
    
    /**
     * Create walkthrough panel
     */
    createWalkthroughPanel() {
        // Create collapsible walkthrough on the left side
        const walkthroughContainer = this.add.container(20, 540);
        walkthroughContainer.setDepth(90);
        
        // Toggle button
        const toggleBg = this.add.graphics();
        toggleBg.fillStyle(0x000000, 0.9);
        toggleBg.lineStyle(2, 0x00ff00, 1);
        toggleBg.fillRoundedRect(0, -30, 40, 60, 5);
        toggleBg.strokeRoundedRect(0, -30, 40, 60, 5);
        walkthroughContainer.add(toggleBg);
        
        const toggleText = this.add.text(20, 0, '›', {
            fontSize: '24px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        walkthroughContainer.add(toggleText);
        
        // Make toggle interactive
        toggleBg.setInteractive(new Phaser.Geom.Rectangle(0, -30, 40, 60), Phaser.Geom.Rectangle.Contains);
        
        // Walkthrough panel (initially hidden)
        const panelWidth = 350;
        const panelHeight = 600;
        
        const panelBg = this.add.graphics();
        panelBg.fillStyle(0x000000, 0.95);
        panelBg.lineStyle(2, 0x00ff00, 1);
        panelBg.fillRoundedRect(50, -panelHeight/2, panelWidth, panelHeight, 10);
        panelBg.strokeRoundedRect(50, -panelHeight/2, panelWidth, panelHeight, 10);
        panelBg.setVisible(false);
        walkthroughContainer.add(panelBg);
        
        // Create content container as child of walkthrough container
        const contentContainer = this.add.container(70, -panelHeight/2 + 20);
        contentContainer.setVisible(false);
        walkthroughContainer.add(contentContainer);
        
        // Title
        const title = this.add.text(0, 0, 'GAME WALKTHROUGH', {
            fontSize: '18px',
            fontFamily: 'Courier New',
            color: '#00ffff',
            fontWeight: 'bold'
        });
        contentContainer.add(title);
        
        // Player Mode section
        const playerTitle = this.add.text(0, 40, 'PLAYER MODE', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        });
        contentContainer.add(playerTitle);
        
        const playerText = this.add.text(0, 65, 
            '• Entry: 100 tokens\n' +
            '• Find M token in 5 min\n' +
            '• Random movement\n' +
            '• Winner takes 90%\n' +
            '• 5 Skills available', {
            fontSize: '14px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            lineSpacing: 3
        });
        contentContainer.add(playerText);
        
        // Spectator Mode section
        const spectatorTitle = this.add.text(0, 180, 'SPECTATOR MODE', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        });
        contentContainer.add(spectatorTitle);
        
        const spectatorText = this.add.text(0, 205,
            '• Bet: 200 tokens fixed\n' +
            '• Pick a player to win\n' +
            '• Winners split 95%\n' +
            '• Watch race live\n' +
            '• Check player stats', {
            fontSize: '14px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            lineSpacing: 3
        });
        contentContainer.add(spectatorText);
        
        // Tips section with better spacing and visibility
        const tipsTitle = this.add.text(0, 320, 'PRO TIPS', {
            fontSize: '18px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        });
        contentContainer.add(tipsTitle);
        
        const tipsText = this.add.text(0, 350,
            '• Fewer players = better odds\n' +
            '• AI players pay 50% entry\n' +
            '• Collect boosters for speed\n' +
            '• Save skills for key moments\n' +
            '• Study win rates before betting', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffffff',  // Changed to white for better visibility
            lineSpacing: 8,    // Increased line spacing to prevent overlap
            fontWeight: 'bold'
        });
        contentContainer.add(tipsText);
        
        // Token info
        const tokenTitle = this.add.text(0, 460, 'TOKENS', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        });
        contentContainer.add(tokenTitle);
        
        const tokenText = this.add.text(0, 485,
            '• Start: 10,000 tokens\n' +
            '• Emergency loan: 500\n' +
            '• 50% auto-repayment', {
            fontSize: '14px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            lineSpacing: 3
        });
        contentContainer.add(tokenText);
        
        // Store references
        this.walkthroughExpanded = false;
        this.walkthroughPanel = panelBg;
        this.walkthroughContent = contentContainer;
        this.walkthroughToggle = toggleText;
        
        // Toggle functionality
        toggleBg.on('pointerdown', () => {
            this.toggleWalkthrough();
        });
        
        // Add hover effect
        toggleBg.on('pointerover', () => {
            toggleBg.clear();
            toggleBg.fillStyle(0x003300, 0.9);
            toggleBg.lineStyle(2, 0x00ff00, 1);
            toggleBg.fillRoundedRect(0, -30, 40, 60, 5);
            toggleBg.strokeRoundedRect(0, -30, 40, 60, 5);
        });
        
        toggleBg.on('pointerout', () => {
            toggleBg.clear();
            toggleBg.fillStyle(0x000000, 0.9);
            toggleBg.lineStyle(2, 0x00ff00, 1);
            toggleBg.fillRoundedRect(0, -30, 40, 60, 5);
            toggleBg.strokeRoundedRect(0, -30, 40, 60, 5);
        });
    }
    
    /**
     * Toggle walkthrough panel visibility
     */
    toggleWalkthrough() {
        this.walkthroughExpanded = !this.walkthroughExpanded;
        
        if (this.walkthroughExpanded) {
            this.walkthroughPanel.setVisible(true);
            this.walkthroughContent.setVisible(true);
            this.walkthroughToggle.setText('‹');
            
            // Reset alpha for fade in
            this.walkthroughPanel.setAlpha(0);
            this.walkthroughContent.setAlpha(0);
            
            // Fade in animation
            this.tweens.add({
                targets: [this.walkthroughPanel, this.walkthroughContent],
                alpha: 1,
                duration: 300,
                ease: 'Power2'
            });
        } else {
            // Fade out animation
            this.tweens.add({
                targets: [this.walkthroughPanel, this.walkthroughContent],
                alpha: 0,
                duration: 300,
                ease: 'Power2',
                onComplete: () => {
                    this.walkthroughPanel.setVisible(false);
                    this.walkthroughContent.setVisible(false);
                }
            });
            
            this.walkthroughToggle.setText('›');
        }
    }
    
    /**
     * Create fallback UI when main create fails
     * @param {Error} error - The error that caused the fallback
     */
    createFallbackUI(error) {
        try {
            console.log('[MainMenuScene] Creating fallback UI...');
            
            // Simple background
            this.cameras.main.setBackgroundColor('#001122');
            
            // Error title
            this.add.text(960, 300, 'MEMEX RACING', {
                fontSize: '96px',
                fontFamily: 'Courier New',
                color: '#00ff00',
                fontWeight: 'bold'
            }).setOrigin(0.5);
            
            // Error message
            this.add.text(960, 450, 'LOADING ERROR OCCURRED', {
                fontSize: '32px',
                fontFamily: 'Courier New',
                color: '#ff4444',
                fontWeight: 'bold'
            }).setOrigin(0.5);
            
            // Simple button to retry
            const retryButton = this.add.text(960, 600, 'RELOAD PAGE', {
                fontSize: '24px',
                fontFamily: 'Courier New',
                color: '#ffffff',
                backgroundColor: '#ff4444',
                padding: { x: 20, y: 10 }
            }).setOrigin(0.5).setInteractive();
            
            retryButton.on('pointerdown', () => {
                window.location.reload();
            });
            
            // Error details for debugging
            if (process.env.NODE_ENV !== 'production') {
                this.add.text(960, 750, `Error: ${error.message}`, {
                    fontSize: '16px',
                    fontFamily: 'Courier New',
                    color: '#ffff00'
                }).setOrigin(0.5);
            }
            
            console.log('[MainMenuScene] ✅ Fallback UI created');
            
        } catch (fallbackError) {
            console.error('[MainMenuScene] ❌ Failed to create fallback UI:', fallbackError);
            // Last resort - just set background
            this.cameras.main.setBackgroundColor('#ff0000');
        }
    }

    /**
     * Create vibrant meme-style background
     */
    createVibrantMemeBackground() {
        // Set base gradient background
        const graphics = this.add.graphics();
        
        // Create rainbow gradient effect
        const colors = [
            { color: 0xff00ff, stop: 0 },    // Magenta
            { color: 0x00ffff, stop: 0.2 },  // Cyan
            { color: 0xffff00, stop: 0.4 },  // Yellow
            { color: 0x00ff00, stop: 0.6 },  // Green
            { color: 0xff0088, stop: 0.8 },  // Pink
            { color: 0x8800ff, stop: 1 }     // Purple
        ];
        
        // Draw diagonal gradient stripes with proper depth
        for (let i = 0; i < 20; i++) {
            const colorIndex = i % colors.length;
            graphics.fillStyle(colors[colorIndex].color, 0.15);
            graphics.fillRect(i * 100 - 200, 0, 300, 1080);
            
            // Animate the stripes with proper depth
            const stripe = this.add.graphics();
            stripe.fillStyle(colors[colorIndex].color, 0.05);
            stripe.fillRect(0, 0, 100, 1080);
            stripe.x = i * 100;
            stripe.setDepth(-100); // Set stripes to background depth
            
            this.tweens.add({
                targets: stripe,
                x: stripe.x + 100,
                duration: 5000 + (i * 200),
                repeat: -1,
                ease: 'Linear'
            });
        }
        
        // Set graphics depth to background
        graphics.setDepth(-100);
        
        // Add floating meme elements
        const memeEmojis = ['🚀', '🌙', '💎', '🦍', '📈', '🔥', '⚡', '🏁'];
        for (let i = 0; i < 15; i++) {
            const x = Math.random() * 1920;
            const y = Math.random() * 1080;
            const emoji = this.add.text(x, y, memeEmojis[Math.floor(Math.random() * memeEmojis.length)], {
                fontSize: '48px'
            }).setAlpha(0.3).setDepth(-90); // Set emojis slightly above background
            
            this.tweens.add({
                targets: emoji,
                x: x + Phaser.Math.Between(-200, 200),
                y: y + Phaser.Math.Between(-200, 200),
                rotation: Phaser.Math.Between(-1, 1),
                alpha: { from: 0.1, to: 0.4 },
                duration: Phaser.Math.Between(5000, 10000),
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });
        }
        
        // Add pulsing grid overlay
        const gridGraphics = this.add.graphics();
        gridGraphics.lineStyle(1, 0x00ff00, 0.1);
        for (let x = 0; x < 1920; x += 50) {
            gridGraphics.moveTo(x, 0);
            gridGraphics.lineTo(x, 1080);
        }
        for (let y = 0; y < 1080; y += 50) {
            gridGraphics.moveTo(0, y);
            gridGraphics.lineTo(1920, y);
        }
        gridGraphics.strokePath();
        gridGraphics.setDepth(-8);
        
        // Pulse the grid
        this.tweens.add({
            targets: gridGraphics,
            alpha: { from: 0.05, to: 0.15 },
            duration: 2000,
            yoyo: true,
            repeat: -1
        });
    }

    /**
     * Create animated background elements
     */
    createAnimatedBackground() {
        // Create moving stars/particles
        for (let i = 0; i < 50; i++) {
            const star = this.add.circle(
                Phaser.Math.Between(0, 1920),
                Phaser.Math.Between(0, 1080),
                Phaser.Math.Between(1, 3),
                0xffffff,
                Phaser.Math.FloatBetween(0.3, 0.8)
            );
            
            // Animate star movement
            this.tweens.add({
                targets: star,
                x: star.x + Phaser.Math.Between(-100, 100),
                y: star.y + Phaser.Math.Between(-100, 100),
                duration: Phaser.Math.Between(3000, 8000),
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });
            
            // Animate star twinkling
            this.tweens.add({
                targets: star,
                alpha: Phaser.Math.FloatBetween(0.1, 1),
                duration: Phaser.Math.Between(1000, 3000),
                yoyo: true,
                repeat: -1
            });
        }
        
        // Add racing track elements in background
        const trackGraphics = this.add.graphics();
        trackGraphics.lineStyle(4, 0x333333, 0.3);
        
        // Draw some track-like lines
        for (let i = 0; i < 5; i++) {
            const y = 200 + i * 150;
            trackGraphics.beginPath();
            trackGraphics.moveTo(0, y);
            trackGraphics.lineTo(1920, y);
            trackGraphics.strokePath();
        }
    }

    /**
     * Create main menu buttons with reduced size and spacing
     */
    createMenuButtons() {
        const buttonY = 480;  // Increased from 420 to add space after announcement panel
        const buttonSpacing = 96;  // Reduced from 120 (20% reduction)
        
        // Quick Play button with instruction
        const quickPlayButton = this.createMenuButton(960, buttonY, 'QUICK PLAY', 'button_green', () => {
            this.startQuickPlay();
        }, 'Start racing instantly with AI opponents (15s countdown)');
        
        // Create Room button with instruction
        const createRoomButton = this.createMenuButton(960, buttonY + buttonSpacing, 'CREATE ROOM', 'button_blue', () => {
            this.createRoom();
        }, 'Host a private room - share the code with friends');
        
        // Join Room button with instruction
        const joinRoomButton = this.createMenuButton(960, buttonY + buttonSpacing * 2, 'JOIN ROOM', 'button_blue', () => {
            this.showJoinRoomDialog();
        }, 'Enter a room code to join an existing game');
        
        // Spectator Mode button (Dashboard)
        const dashboardButton = this.createMenuButton(960, buttonY + buttonSpacing * 3, 'SPECTATOR MODE', 'button_yellow', () => {
            this.openCommandCenter();
        }, 'Watch live races and place bets as a spectator');
        
        // Gameplay Guide button
        const guideButton = this.createMenuButton(960, buttonY + buttonSpacing * 4, 'GAMEPLAY GUIDE', 'button_purple', () => {
            this.showGameplayGuide();
        }, 'Complete walkthrough with detailed game mechanics');
        
        // Settings button
        const settingsButton = this.createMenuButton(960, buttonY + buttonSpacing * 5, 'SETTINGS', 'button_red', () => {
            this.showSettings();
        }, 'Configure game options and preferences');
        
        this.menuOptions = [quickPlayButton, createRoomButton, joinRoomButton, dashboardButton, guideButton, settingsButton];
        
        // Highlight first option
        this.highlightOption(0);
    }

    /**
     * Create a menu button
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {string} text - Button text
     * @param {string} spriteKey - Button sprite key
     * @param {Function} callback - Click callback
     * @returns {Object} Button object
     */
    createMenuButton(x, y, text, spriteKey, callback, helpText = '') {
        // Button background - check if texture exists, otherwise create a rectangle
        let button;
        if (this.textures.exists(spriteKey)) {
            button = this.add.image(x, y, spriteKey);
        } else {
            // Fallback to rectangle if sprite doesn't exist
            const colors = {
                'button_green': 0x00ff00,
                'button_blue': 0x0000ff,
                'button_red': 0xff0000,
                'button_yellow': 0xffff00,
                'button_purple': 0x8800ff
            };
            button = this.add.rectangle(x, y, 240, 64, 0x000000, 0.3); // Reduced from 300x80 (20% reduction)
            button.setStrokeStyle(3, colors[spriteKey] || 0x666666);
        }
        button.setInteractive({ useHandCursor: true });
        button.setScale(1.2);  // Reduced from 1.5 (20% reduction)
        
        // Button text with better retro styling
        const buttonText = this.add.text(x, y, text, {
            fontSize: '22px',  // Reduced from 28px
            fontFamily: 'Consolas, "Courier New", monospace',
            color: '#ffffff',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 3,
            shadow: {
                offsetX: 1,
                offsetY: 1,
                color: '#000000',
                blur: 2,
                stroke: true,
                fill: true
            }
        }).setOrigin(0.5);
        
        // Add help text if provided
        let helpTextObj = null;
        if (helpText) {
            helpTextObj = this.add.text(x, y + 40, helpText, {
                fontSize: '14px',
                fontFamily: 'Courier New',
                color: '#ffffff',  // Changed from grey to white for better visibility
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 2
            }).setOrigin(0.5);
            helpTextObj.setVisible(false);
        }
        
        // Hover effects (merged with help text visibility)
        button.on('pointerover', () => {
            button.setScale(1.3);  // Reduced from 1.6 to match the smaller button scale
            buttonText.setColor('#ffff00');
            if (helpTextObj) helpTextObj.setVisible(true);
            this.playSafeAudio('ui_hover', { volume: 0.3 });
        });
        
        button.on('pointerout', () => {
            button.setScale(1.2);  // Reduced from 1.5 to match the smaller button scale
            buttonText.setColor('#ffffff');
            if (helpTextObj) helpTextObj.setVisible(false);
        });
        
        // Click handler
        button.on('pointerdown', () => {
            console.log(`[MainMenuScene] Button clicked: ${text}`);
            this.playSafeAudio('ui_click', { volume: 0.5 });
            button.setScale(1.1);  // Slightly smaller scale on click
            if (helpTextObj) helpTextObj.setVisible(false);
            
            // Execute callback immediately without delay to prevent timing issues
            try {
                if (typeof callback === 'function') {
                    callback();
                } else {
                    console.error(`[MainMenuScene] Button callback not found for: ${text}`);
                }
            } catch (error) {
                console.error(`[MainMenuScene] Error executing button callback for ${text}:`, error);
            }
            
            // Reset scale after a short delay
            this.time.delayedCall(100, () => {
                button.setScale(1.2);  // Reset to original scale
            });
        });
        
        return { button, text: buttonText, callback };
    }

    /**
     * Create user info panel using AuthenticatedScene methods
     */
    createUserInfoPanel() {
        // First check for dashboard player
        const dashboardPlayer = JSON.parse(sessionStorage.getItem('currentPlayer') || 'null');
        let username;
        
        if (dashboardPlayer && dashboardPlayer.playerName) {
            // Use dashboard player name and store it for consistency
            username = dashboardPlayer.playerName;
            localStorage.setItem('memexRacingUsername', username);
            this.game.playerUsername = username;
            console.log('[MainMenuScene] Using dashboard player:', username);
        } else {
            // Fallback to stored username or game data
            username = localStorage.getItem('memexRacingUsername') || this.game.playerUsername || 'Guest';
        }
        
        // Get stats from localStorage (or initialize defaults)
        const stats = JSON.parse(localStorage.getItem('memexRacingStats') || '{}');
        
        this.userInfo = {
            username: username,
            coins: stats.coins || 1000,
            wins: stats.wins || 0,
            totalRaces: stats.totalRaces || 0
        };
        
        this.displayUserInfo();
    }

    /**
     * Display authenticated user information
     */
    displayUserInfo() {
        const panelX = 1600;
        const panelY = 150;
        
        // User panel background
        const panelBg = this.add.graphics();
        panelBg.fillStyle(0x000000, 0.8);
        panelBg.fillRoundedRect(panelX - 150, panelY - 50, 300, 200, 10);
        // Removed green border lines for cleaner UI
        
        // User info text with better visibility
        this.add.text(panelX, panelY - 20, `${this.userInfo.username || 'Player'}`, {
            fontSize: '22px',
            fontFamily: 'Consolas, "Courier New", monospace',
            color: '#00ff00',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        this.add.text(panelX, panelY + 10, `Coins: ${this.userInfo.coins || 1000}`, {
            fontSize: '18px',
            fontFamily: 'Consolas, "Courier New", monospace',
            color: '#ffff00',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        this.add.text(panelX, panelY + 35, `Wins: ${this.userInfo.wins || 0}`, {
            fontSize: '18px',
            fontFamily: 'Consolas, "Courier New", monospace',
            color: '#ffffff',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        this.add.text(panelX, panelY + 60, `Races: ${this.userInfo.totalRaces || 0}`, {
            fontSize: '18px',
            fontFamily: 'Consolas, "Courier New", monospace',
            color: '#ffffff',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 2
        }).setOrigin(0.5);
        
        // Logout button
        const logoutButton = this.add.text(panelX, panelY + 100, 'LOGOUT', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ff0000',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        logoutButton.setInteractive({ useHandCursor: true });
        logoutButton.on('pointerdown', () => {
            // Clear localStorage and go back to login
            localStorage.removeItem('memexRacingUsername');
            this.scene.start('LoginScene');
        });
    }


    /**
     * Setup input handlers for keyboard navigation
     */
    setupInputHandlers() {
        // Keyboard navigation
        this.input.keyboard.on('keydown-UP', () => {
            this.selectedOption = Math.max(0, this.selectedOption - 1);
            this.highlightOption(this.selectedOption);
            this.playSafeAudio('ui_move', { volume: 0.2 });
        });
        
        this.input.keyboard.on('keydown-DOWN', () => {
            this.selectedOption = Math.min(this.menuOptions.length - 1, this.selectedOption + 1);
            this.highlightOption(this.selectedOption);
            this.playSafeAudio('ui_move', { volume: 0.2 });
        });
        
        this.input.keyboard.on('keydown-ENTER', () => {
            this.selectOption(this.selectedOption);
        });
        
        this.input.keyboard.on('keydown-ESC', () => {
            this.showSettings();
        });
        
        // Add shortcut keys for gameplay guide
        this.input.keyboard.on('keydown-H', () => {
            this.showGameplayGuide();
        });
        
        this.input.keyboard.on('keydown-G', () => {
            this.showGameplayGuide();
        });
    }

    /**
     * Highlight selected menu option
     * @param {number} index - Option index
     */
    highlightOption(index) {
        this.menuOptions.forEach((option, i) => {
            if (i === index) {
                option.button.setScale(1.6);
                option.text.setColor('#ffff00');
            } else {
                option.button.setScale(1.5);
                option.text.setColor('#ffffff');
            }
        });
    }

    /**
     * Select menu option
     * @param {number} index - Option index
     */
    selectOption(index) {
        if (index >= 0 && index < this.menuOptions.length) {
            this.playSafeAudio('ui_select', { volume: 0.5 });
            this.menuOptions[index].callback();
        }
    }

    /**
     * Start quick play mode
     */
    startQuickPlay() {
        console.log('[MainMenuScene] Starting Quick Play');
        
        try {
            // Create a single-player room
            const roomData = {
                roomId: `quick_${Date.now()}`,
                playerCount: 1,
                maxPlayers: 6,
                gameMode: 'quickplay',
                host: this.getUsername() || 'Guest'
            };
            
            console.log('[MainMenuScene] Room data created:', roomData);
            
            // Check if LobbyScene exists before transitioning
            if (this.scene.manager.getScene('LobbyScene')) {
                console.log('[MainMenuScene] Transitioning to LobbyScene...');
                this.scene.start('LobbyScene', { roomData, isQuickPlay: true });
                console.log('[MainMenuScene] Scene transition initiated successfully');
            } else {
                console.error('[MainMenuScene] LobbyScene not found in scene manager');
            }
            
            // Emit event for multiplayer system
            this.events.emit('ROOM_CREATED', roomData);
            console.log('[MainMenuScene] ROOM_CREATED event emitted');
            
        } catch (error) {
            console.error('[MainMenuScene] Error in startQuickPlay:', error);
        }
    }

    /**
     * Create a new multiplayer room
     */
    createRoom() {
        console.log('[MainMenuScene] Creating Room');
        
        // Generate room code
        const roomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
        
        const roomData = {
            roomId: roomCode,
            playerCount: 1,
            maxPlayers: 6,
            gameMode: 'multiplayer',
            host: this.getUsername() || 'Guest',
            created: Date.now()
        };
        
        // Transition to lobby
        this.scene.start('LobbyScene', { roomData, isHost: true });
        
        // Emit event for multiplayer system
        this.events.emit('ROOM_CREATED', roomData);
    }

    /**
     * Show join room dialog
     */
    showJoinRoomDialog() {
        console.log('[MainMenuScene] Showing Join Room Dialog');
        
        // Create modal background
        const modal = this.add.graphics();
        modal.fillStyle(0x000000, 0.8);
        modal.fillRect(0, 0, 1920, 1080);
        
        // Dialog box
        const dialogBg = this.add.graphics();
        dialogBg.fillStyle(0x000000, 0.9);
        dialogBg.fillRoundedRect(660, 390, 600, 300, 20);
        // Removed green border lines for cleaner UI
        
        // Dialog title
        this.add.text(960, 450, 'JOIN ROOM', {
            fontSize: '32px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Input field simulation (HTML input would be created in real implementation)
        const inputBg = this.add.graphics();
        inputBg.fillStyle(0x333333);
        inputBg.fillRoundedRect(760, 500, 400, 50, 8);
        inputBg.lineStyle(2, 0xffffff);
        inputBg.strokeRoundedRect(760, 500, 400, 50, 8);
        
        const inputText = this.add.text(960, 525, 'Enter Room Code...', {
            fontSize: '20px',
            fontFamily: 'Courier New',
            color: '#888888'
        }).setOrigin(0.5);
        
        // Buttons - properly spaced to prevent overlap
        const joinButton = this.createMenuButton(860, 620, 'JOIN', 'button_green', () => {
            // In real implementation, get room code from input
            const roomCode = 'DEMO123';
            this.joinRoom(roomCode);
            this.closeDialog([modal, dialogBg, inputBg, inputText, joinButton.button, joinButton.text, cancelButton.button, cancelButton.text]);
        });
        
        const cancelButton = this.createMenuButton(1060, 620, 'CANCEL', 'button_red', () => {
            this.closeDialog([modal, dialogBg, inputBg, inputText, joinButton.button, joinButton.text, cancelButton.button, cancelButton.text]);
        });
        
        // Store dialog elements for cleanup
        this.dialogElements = [modal, dialogBg, inputBg, inputText, joinButton.button, joinButton.text, cancelButton.button, cancelButton.text];
    }

    /**
     * Join an existing room
     * @param {string} roomCode - Room code to join
     */
    joinRoom(roomCode) {
        console.log(`[MainMenuScene] Joining Room: ${roomCode}`);
        
        // In real implementation, validate room exists
        const roomData = {
            roomId: roomCode,
            playerCount: 2, // Example
            maxPlayers: 6,
            gameMode: 'multiplayer',
            host: 'SomePlayer'
        };
        
        // Transition to lobby
        this.scene.start('LobbyScene', { roomData, isHost: false });
        
        // Emit event for multiplayer system
        this.events.emit('PLAYER_JOINED', { roomId: roomCode, player: this.getUsername() || 'Guest' });
    }

    /**
     * Open Spectator Mode Dashboard in new window
     */
    openCommandCenter() {
        console.log('[MainMenuScene] Opening Spectator Mode Dashboard');
        
        try {
            // Get user context for dashboard initialization
            const userContext = this.getUser();
            const userData = {
                username: userContext?.username || 'Guest Player',
                balance: userContext?.statistics?.totalPoints || 10000,
                isAuthenticated: !!userContext,
                wins: userContext?.statistics?.wins || 0,
                totalRaces: userContext?.statistics?.gamesPlayed || 0
            };
            
            // Store user data for dashboard access
            localStorage.setItem('m1-dashboard-user', JSON.stringify(userData));
            localStorage.setItem('m1-dashboard-launched-from-game', 'true');
            localStorage.setItem('m1-dashboard-launch-time', Date.now().toString());
            
            // Open dashboard in new window
            const dashboardWindow = window.open(
                'main-dashboard.html',
                'M1CommandCenter',
                'width=1400,height=900,scrollbars=no,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
            );
            
            if (dashboardWindow) {
                console.log('[MainMenuScene] ✅ Spectator Mode opened successfully');
                
                // Focus the dashboard window
                dashboardWindow.focus();
                
                // Optional: Listen for dashboard messages
                this.setupDashboardCommunication(dashboardWindow);
                
                // Play success sound
                this.playSafeAudio('ui_select', { volume: 0.5 });
                
                // Track dashboard launch for analytics
                this.events.emit('DASHBOARD_LAUNCHED', {
                    username: userData.username,
                    launchTime: Date.now(),
                    fromScene: 'MainMenuScene'
                });
                
            } else {
                console.warn('[MainMenuScene] ❌ Failed to open Spectator Mode (popup blocked?)');
                this.showDashboardAlert('Popup blocked! Please allow popups for Spectator Mode.');
            }
            
        } catch (error) {
            console.error('[MainMenuScene] ❌ Error opening Spectator Mode:', error);
            this.showDashboardAlert('Failed to open Spectator Mode. Please try again.');
        }
    }
    
    /**
     * Setup communication with dashboard window
     * @param {Window} dashboardWindow - Reference to dashboard window
     */
    setupDashboardCommunication(dashboardWindow) {
        // Listen for messages from dashboard
        const messageHandler = (event) => {
            // Validate origin for security (in production, use specific domain)
            if (event.origin !== window.location.origin) return;
            
            try {
                const message = event.data;
                if (message.type === 'DASHBOARD_REQUEST_USER_DATA') {
                    // Send current user data to dashboard
                    const userContext = this.getUser();
                    dashboardWindow.postMessage({
                        type: 'USER_DATA_UPDATE',
                        userData: {
                            username: userContext?.username || 'Guest Player',
                            balance: userContext?.statistics?.totalPoints || 10000,
                            isAuthenticated: !!userContext,
                            wins: userContext?.statistics?.wins || 0,
                            totalRaces: userContext?.statistics?.gamesPlayed || 0
                        }
                    }, window.location.origin);
                }
                
                if (message.type === 'DASHBOARD_CLOSED') {
                    window.removeEventListener('message', messageHandler);
                    console.log('[MainMenuScene] Dashboard communication closed');
                }
                
            } catch (error) {
                console.warn('[MainMenuScene] Error handling dashboard message:', error);
            }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Send initial user data
        setTimeout(() => {
            const userContext = this.getUser();
            if (dashboardWindow && !dashboardWindow.closed) {
                dashboardWindow.postMessage({
                    type: 'INITIAL_USER_DATA',
                    userData: {
                        username: userContext?.username || 'Guest Player',
                        balance: userContext?.statistics?.totalPoints || 10000,
                        isAuthenticated: !!userContext,
                        wins: userContext?.statistics?.wins || 0,
                        totalRaces: userContext?.statistics?.gamesPlayed || 0
                    }
                }, window.location.origin);
            }
        }, 1000);
    }
    
    /**
     * Show dashboard-related alert
     * @param {string} message - Alert message
     */
    showDashboardAlert(message) {
        // Create alert modal
        const modal = this.add.graphics();
        modal.fillStyle(0x000000, 0.9);
        modal.fillRect(0, 0, 1920, 1080);
        
        const alertBg = this.add.graphics();
        alertBg.fillStyle(0x1a1a1a, 0.95);
        alertBg.fillRoundedRect(660, 390, 600, 300, 20);
        alertBg.lineStyle(4, 0xffff00);
        alertBg.strokeRoundedRect(660, 390, 600, 300, 20);
        
        // Alert title
        this.add.text(960, 450, 'SPECTATOR MODE', {
            fontSize: '32px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Alert message
        this.add.text(960, 520, message, {
            fontSize: '18px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            wordWrap: { width: 500 },
            align: 'center'
        }).setOrigin(0.5);
        
        // Close button
        const closeButton = this.createMenuButton(960, 600, 'OK', 'button_green', () => {
            modal.destroy();
            alertBg.destroy();
        });
        
        // Auto-close after 5 seconds
        this.time.delayedCall(5000, () => {
            if (modal.active) {
                modal.destroy();
                alertBg.destroy();
            }
        });
    }

    /**
     * Show gameplay guide
     */
    showGameplayGuide() {
        console.log('[MainMenuScene] Showing gameplay guide');
        if (this.gameplayGuide) {
            this.gameplayGuide.show();
        } else {
            console.error('[MainMenuScene] GameplayGuide not initialized');
        }
    }

    /**
     * Show settings menu
     */
    showSettings() {
        console.log('[MainMenuScene] Showing Settings');
        
        // Create settings overlay container
        if (this.settingsOverlay) {
            this.settingsOverlay.destroy();
        }
        
        // Position settings panel in the center of the screen
        const centerX = this.cameras.main.centerX;
        const centerY = this.cameras.main.centerY;
        
        this.settingsOverlay = this.add.container(centerX, centerY);
        this.settingsOverlay.setDepth(200);
        
        // Semi-transparent background
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.8);
        overlay.fillRect(-centerX, -centerY, 1920, 1080);  // Use fixed dimensions
        this.settingsOverlay.add(overlay);
        
        // Settings panel - moved up slightly to ensure it's fully visible
        const panelWidth = 400;
        const panelHeight = 300;
        
        const panel = this.add.graphics();
        panel.fillStyle(0x000000, 0.95);
        panel.lineStyle(3, 0x00ff00, 1);
        panel.fillRoundedRect(-panelWidth/2, -panelHeight/2, panelWidth, panelHeight, 10);
        panel.strokeRoundedRect(-panelWidth/2, -panelHeight/2, panelWidth, panelHeight, 10);
        this.settingsOverlay.add(panel);
        
        // Title
        const title = this.add.text(0, -panelHeight/2 + 30, '⚙️ SETTINGS', {
            fontSize: '24px',
            fontFamily: 'Courier New',
            color: '#00ffff',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        this.settingsOverlay.add(title);
        
        // Language section title
        const langTitle = this.add.text(0, -60, 'LANGUAGE / 语言 / 언어', {
            fontSize: '18px',
            fontFamily: 'Courier New',
            color: '#ffff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        this.settingsOverlay.add(langTitle);
        
        // Get current language from localStorage
        const currentLang = localStorage.getItem('memex-racing-language') || 'en';
        
        // Language options
        const languages = [
            { code: 'en', name: 'English', flag: '🇬🇧' },
            { code: 'zh', name: '中文', flag: '🇨🇳' },
            { code: 'ko', name: '한국어', flag: '🇰🇷' }
        ];
        
        // Create language buttons
        languages.forEach((lang, index) => {
            const y = -10 + (index * 50);
            
            // Button background
            const buttonBg = this.add.graphics();
            const isSelected = currentLang === lang.code;
            
            if (isSelected) {
                buttonBg.fillStyle(0x00ff00, 0.2);
                buttonBg.lineStyle(2, 0x00ff00, 1);
            } else {
                buttonBg.fillStyle(0x000000, 0.5);
                buttonBg.lineStyle(1, 0x00ff00, 0.5);
            }
            
            buttonBg.fillRoundedRect(-150, y - 20, 300, 40, 5);
            buttonBg.strokeRoundedRect(-150, y - 20, 300, 40, 5);
            this.settingsOverlay.add(buttonBg);
            
            // Language text
            const langText = this.add.text(0, y, `${lang.flag} ${lang.name}`, {
                fontSize: '20px',
                fontFamily: 'Courier New',
                color: isSelected ? '#00ff00' : '#ffffff',
                fontWeight: isSelected ? 'bold' : 'normal'
            }).setOrigin(0.5);
            this.settingsOverlay.add(langText);
            
            // Make button interactive
            buttonBg.setInteractive(new Phaser.Geom.Rectangle(-150, y - 20, 300, 40), Phaser.Geom.Rectangle.Contains);
            
            buttonBg.on('pointerdown', () => {
                // Save language preference
                localStorage.setItem('memex-racing-language', lang.code);
                console.log(`[Settings] Language changed to: ${lang.name}`);
                
                // Refresh settings to show selection
                this.showSettings();
                
                // In a real implementation, this would trigger language change throughout the game
                this.applyLanguageChange(lang.code);
            });
            
            buttonBg.on('pointerover', () => {
                if (!isSelected) {
                    buttonBg.clear();
                    buttonBg.fillStyle(0x00ff00, 0.1);
                    buttonBg.lineStyle(2, 0x00ff00, 0.8);
                    buttonBg.fillRoundedRect(-150, y - 20, 300, 40, 5);
                    buttonBg.strokeRoundedRect(-150, y - 20, 300, 40, 5);
                }
            });
            
            buttonBg.on('pointerout', () => {
                if (!isSelected) {
                    buttonBg.clear();
                    buttonBg.fillStyle(0x000000, 0.5);
                    buttonBg.lineStyle(1, 0x00ff00, 0.5);
                    buttonBg.fillRoundedRect(-150, y - 20, 300, 40, 5);
                    buttonBg.strokeRoundedRect(-150, y - 20, 300, 40, 5);
                }
            });
        });
        
        // Close button
        const closeBtn = this.add.text(panelWidth/2 - 30, -panelHeight/2 + 30, '✕', {
            fontSize: '24px',
            fontFamily: 'Courier New',
            color: '#ff0000',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        closeBtn.setInteractive();
        closeBtn.on('pointerdown', () => this.closeSettings());
        closeBtn.on('pointerover', () => closeBtn.setScale(1.2));
        closeBtn.on('pointerout', () => closeBtn.setScale(1));
        this.settingsOverlay.add(closeBtn);
        
        // Make overlay interactive to close on click outside
        overlay.setInteractive(new Phaser.Geom.Rectangle(-960, -540, 1920, 1080), Phaser.Geom.Rectangle.Contains);
        overlay.on('pointerdown', (pointer, localX, localY) => {
            // Check if click is outside the panel
            if (Math.abs(localX) > panelWidth/2 || Math.abs(localY) > panelHeight/2) {
                this.closeSettings();
            }
        });
        
        // ESC key to close
        this.input.keyboard.once('keydown-ESC', () => {
            this.closeSettings();
        });
    }
    
    /**
     * Close settings menu
     */
    closeSettings() {
        if (this.settingsOverlay) {
            this.settingsOverlay.destroy();
            this.settingsOverlay = null;
        }
    }
    
    /**
     * Apply language change
     * @param {string} langCode - Language code (en, zh, ko)
     */
    applyLanguageChange(langCode) {
        // This is where you would update all text in the game
        // For now, just log the change
        console.log(`[Settings] Applying language: ${langCode}`);
        
        // You could emit an event for other scenes to listen to
        this.events.emit('languageChanged', langCode);
        
        // Store globally for other scenes to access
        if (this.game.registry) {
            this.game.registry.set('language', langCode);
        }
    }

    /**
     * Show login interface
     */
    showLogin() {
        console.log('[MainMenuScene] Showing Login');
        // Redirect to login page (now in public directory)
        window.location.href = '/login.html';
    }

    /**
     * Show register interface
     */
    showRegister() {
        console.log('[MainMenuScene] Showing Register');
        // Redirect to login page with register tab (now in public directory)
        window.location.href = '/login.html';
    }

    /**
     * Logout current user using AuthenticatedScene method
     */
    logout() {
        console.log('[MainMenuScene] Logging out');
        // Use AuthenticatedScene method to request logout
        this.requestLogout();
        // Refresh scene to show guest info
        this.scene.restart();
    }

    /**
     * Handle authentication context ready (override from AuthenticatedScene)
     */
    onAuthContextReady(userContext, isAuthenticated) {
        console.log('[MainMenuScene] Auth context ready:', { isAuthenticated, username: userContext?.username });
        // Refresh user info panel when auth context is ready
        this.refreshUserInfoPanel();
    }
    
    /**
     * Handle user context updates (override from AuthenticatedScene)
     */
    onUserContextUpdate(userData) {
        console.log('[MainMenuScene] User context updated:', userData?.username);
        // Refresh user info panel when user data changes
        this.refreshUserInfoPanel();
    }
    
    /**
     * Handle auth state changes (override from AuthenticatedScene)
     */
    onAuthStateChange(stateChange) {
        console.log('[MainMenuScene] Auth state changed:', stateChange.type);
        if (stateChange.type === 'logout' || stateChange.type === 'session_expired') {
            // Refresh scene to show guest info
            this.time.delayedCall(100, () => {
                this.scene.restart();
            });
        }
    }
    
    /**
     * Refresh user info panel
     */
    refreshUserInfoPanel() {
        // Avoid scene restart to prevent flashing
        // Instead, just update the user info directly
        console.log('[MainMenuScene] Refreshing user info panel...');
        
        // Only restart if absolutely necessary and scene is fully ready
        if (this.scene.isActive() && !this.isRefreshing) {
            this.isRefreshing = true;
            // For now, skip the restart to prevent flashing
            // this.scene.restart();
            console.log('[MainMenuScene] User info refresh requested - update UI elements directly instead');
            this.isRefreshing = false;
        }
    }

    /**
     * Play ambient music
     */
    playAmbientMusic() {
        this.playSafeAudio('menu_music', { loop: true, volume: 0.3 });
    }

    /**
     * Safe audio playback with fallback handling
     * @param {string} key - Audio key to play
     * @param {Object} config - Audio configuration
     */
    playSafeAudio(key, config = {}) {
        try {
            if (this.sound && this.sound.get(key)) {
                this.sound.play(key, config);
            } else {
                console.warn(`[MainMenuScene] Audio key '${key}' not found, skipping playback`);
            }
        } catch (error) {
            console.warn(`[MainMenuScene] Failed to play audio '${key}':`, error);
        }
    }

    /**
     * Close dialog elements
     * @param {Array} elements - Elements to destroy
     */
    closeDialog(elements) {
        elements.forEach(element => {
            if (element && element.destroy) {
                element.destroy();
            }
        });
    }

    /**
     * Scene cleanup (enhanced with AuthenticatedScene cleanup)
     */
    destroy() {
        // Reset initialization flag
        this.isInitialized = false;
        this.isRefreshing = false;
        
        // Stop any playing sounds
        this.sound.stopAll();
        
        // Stop all tweens to prevent memory leaks
        this.tweens.killAll();
        
        // Clean up gameplay guide
        if (this.gameplayGuide) {
            this.gameplayGuide.hide();
            this.gameplayGuide = null;
        }
        
        // Call parent destroy for auth cleanup
        super.destroy();
    }
}